import { type DependencyList, useCallback, useEffect, useRef, useState } from 'react';

export type AsyncState<T> =
  | { status: 'loading'; data?: T; error?: undefined }
  | { status: 'success'; data: T; error?: undefined }
  | { status: 'error'; data?: T; error: unknown };

/** Runs `fn` on mount / when deps change; `reload()` re-runs it (keeping stale data visible). */
export function useAsync<T>(fn: () => Promise<T>, deps: DependencyList = []) {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });
  const seq = useRef(0);

  const run = useCallback(() => {
    const my = ++seq.current;
    setState((s) => ({ status: 'loading', data: s.data }));
    fn().then(
      (data) => my === seq.current && setState({ status: 'success', data }),
      (error) => my === seq.current && setState((s) => ({ status: 'error', error, data: s.data })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
  }, [run]);

  return { ...state, reload: run };
}
